import _ from 'lodash';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import styles from './EditProgram.module.scss';
import classNames from 'classnames';
import { Alert, Button, CircularProgress, Snackbar } from '@mui/material';
import EditName from '../composites/EditName';
import AddWorkoutsDialog from './AddWorkoutsDialog';
import AddWorkoutDialog from './AddWorkoutDialog';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { ProgramWeek } from './ProgramWeek';
import { ProgramCalendar } from './ProgramCalendar';
import { locales, Day } from '../../constants';

import { startOfWeekSunday, buildWeeksFromStart } from '../../utils/utils';
import { useUnsavedChanges } from '../../providers';
import ContentDrill, {
  useContentDrillHeader,
} from '../composites/ContentDrill';
import EditRoutine from '../Workouts/EditRoutine';

import type {
  AssignedProgram,
  TrainingRoutine,
  TrainingRoutineTemplate,
  WithOptionalId,
} from '../../types';
import type { DraggedWorkoutLocation } from './CalendarWeek';

const texts = locales.en.components.programs.editProgram;

enum ViewModes {
  WEEK = 'week',
  CALENDAR = 'calendar',
}

export interface EditProgramDialogProps {
  onSubmit: (program: WithOptionalId<AssignedProgram>) => Promise<boolean>;
  program: WithOptionalId<AssignedProgram>;
  onClose: () => void;
  proceedAfterSave?: boolean;
}

const EditProgram: React.FC<EditProgramDialogProps> = ({
  onClose,
  onSubmit,
  program: initialProgram,
  proceedAfterSave = true,
}) => {
  // console.log('-------------------------- EditProgram program: ', program);
  const { setUnsavedChanges, confirmExitIfNeeded } = useUnsavedChanges();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAddWorkoutDialogOpen, setIsAddWorkoutDialogOpen] = useState(false);
  const [isAddWorkoutsDialogOpen, setIsAddWorkoutsDialogOpen] = useState(false);
  // week options menu now handled inside CalendarWeek
  // name editing handled by EditName component
  const nameRef = useRef<HTMLInputElement>(null);
  const [programState, setProgramState] =
    useState<WithOptionalId<AssignedProgram>>(initialProgram);
  const [name, setName] = useState(initialProgram.name || '');
  // commit handled via EditName

  const [viewMode, setViewMode] = useState<ViewModes>(ViewModes.CALENDAR);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Day | null>(null);
  const initialProgramRef =
    useRef<WithOptionalId<AssignedProgram>>(initialProgram);
  const programStateRef = useRef(programState);

  useEffect(() => {
    programStateRef.current = programState;
  }, [programState]);

  const updateProgramState = (
    updater:
      | WithOptionalId<AssignedProgram>
      | ((
        prev: WithOptionalId<AssignedProgram>,
      ) => WithOptionalId<AssignedProgram>),
  ) => {
    const next =
      typeof updater === 'function' ? updater(programStateRef.current) : updater;
    programStateRef.current = next;
    setProgramState(next);
  };

  const saveRef = useRef<() => Promise<void> | void>(() => { });

  // Internal template editing drill-in state
  const [editedRoutine, setEditedRoutine] = useState<TrainingRoutine | null>(
    null,
  );
  const [editedWorkoutWeekIndex, setEditedWorkoutWeekIndex] = useState<
    number | null
  >(null);
  const [editedWorkoutDayIndex, setEditedWorkoutDayIndex] = useState<
    number | null
  >(null);
  const [editedWorkoutDay, setEditedWorkoutDay] = useState<Day | null>(null);
  const [isAddNewWorkoutFlow, setIsAddNewWorkoutFlow] =
    useState<boolean>(false);
  const [draggingWorkout, setDraggingWorkout] =
    useState<DraggedWorkoutLocation | null>(null);

  // Override top-level header when drilled into routine editor
  const headerCtx = useContentDrillHeader();
  useEffect(() => {
    nameRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (editedRoutine) {
      headerCtx?.setHeaderOverride?.({
        title: 'Edit Workout',
        onBack: () => confirmExitIfNeeded(() => setEditedRoutine(null)),
      });
    } else {
      headerCtx?.setHeaderOverride?.(null);
    }
    return () => {
      headerCtx?.setHeaderOverride?.(null);
    };
  }, [editedRoutine, headerCtx, confirmExitIfNeeded]);

  // console.log('programState', programState);

  // ----- Derived calendar structures -----
  const isAssigned = Boolean((programState as any).startDate);

  // Sunday-first order for display only
  const displayDaysOrder: Day[] = useMemo(
    () => [
      Day.Sunday,
      Day.Monday,
      Day.Tuesday,
      Day.Wednesday,
      Day.Thursday,
      Day.Friday,
      Day.Saturday,
    ],
    [],
  );

  // Build concrete dates for each week if assigned. Otherwise null.
  const weeksDates: Date[][] | null = useMemo(() => {
    if (!isAssigned) return null;
    const startDate = new Date((programState as any).startDate as string);
    // Always provide an extra year (52 weeks) beyond the last existing workout week
    const baseWeeks = programState.programItems.length || 0;
    const totalWeeks = baseWeeks + 52;
    return buildWeeksFromStart(startDate, totalWeeks);
  }, [isAssigned, programState]);

  // Compute current week index for auto-scroll and default week view
  const currentWeekIndex = useMemo(() => {
    if (!weeksDates) return 0;
    const today = new Date();
    const startSunday = startOfWeekSunday(
      new Date((programState as any).startDate as string),
    );
    const daysDiff = Math.floor(
      (new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      ).getTime() -
        startSunday.getTime()) /
        (1000 * 60 * 60 * 24),
    );
    const idx = Math.floor(Math.max(0, daysDiff) / 7);
    return Math.min(idx, weeksDates.length - 1);
  }, [weeksDates, programState]);

  const calendarContainerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = useCallback(
    async (shouldClose = true) => {
      if (!name) {
        nameRef.current?.focus({ preventScroll: true });
        return;
      }

      setLoading(true);

      const programDeepClone = _.cloneDeep(programStateRef.current);
      const sortedProgramItemsClone = (programDeepClone.programItems || []).map(
        (week) =>
          (week || []).sort(
            (a, b) =>
              displayDaysOrder.indexOf(a?.day as Day) -
              displayDaysOrder.indexOf(b?.day as Day),
          ),
      );

      const sanitizedProgram: WithOptionalId<AssignedProgram> = {
        ...programStateRef.current,
        programItems: sortedProgramItemsClone,
      };

    // console.log('sanitizedProgram', sanitizedProgram);

      if (sanitizedProgram.programItems.length === 0) {
        setErrorMessage('You must fill at least week workouts.');
        setLoading(false);
        return;
      }
      const success = await onSubmit(sanitizedProgram);
      if (success) {
        initialProgramRef.current = sanitizedProgram;
        if (shouldClose) {
          onClose();
        } else {
          setLoading(false);
        }
      } else {
        setErrorMessage(errorMessage || 'An error occurred. Please try again.');
        setLoading(false);
      }
    },
    [name, onSubmit, onClose, displayDaysOrder],
  );

  // Keep latest save handler in a ref for the UnsavedChangesProvider
  saveRef.current = handleSubmit;

  // Register unsaved changes with UnsavedChangesProvider so Dashboard tab changes are intercepted
  useEffect(() => {
    setUnsavedChanges({
      hasUnsavedChanges: () =>
        !_.isEqual(programStateRef.current, initialProgramRef.current),
      save: async () => {
        await saveRef.current();
      },
      proceedAfterSave,
    });
    return () => setUnsavedChanges(null);
  }, [setUnsavedChanges, proceedAfterSave]);

  useEffect(() => {
    if (!weeksDates) return;
    // Scroll to current week on initial visit
    const el = document.getElementById(`week-${currentWeekIndex}`);
    if (el && calendarContainerRef.current) {
      el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    setSelectedWeekIndex(currentWeekIndex);
  }, [weeksDates, currentWeekIndex]);

  const handleAddWorkoutsFromTemplates = (
    workouts: TrainingRoutineTemplate[],
  ) => {
    updateProgramState((prevProgram) => {
      let updatedProgram = { ...prevProgram };
      if (updatedProgram.programItems.length - 1 < selectedWeekIndex) {
        const newWeeks = Array.from(
          {
            length: selectedWeekIndex - updatedProgram.programItems.length + 1,
          },
          () => [],
        );
        updatedProgram = {
          ...updatedProgram,
          programItems: [...updatedProgram.programItems, ...newWeeks],
        };
      }
      const newRoutines = workouts.map(({ routine }) => ({
        routine,
        day: selectedDay,
      }));
      const updatedProgramItems =
        updatedProgram.programItems.length === 0
          ? [newRoutines]
          : updatedProgram.programItems.map((week, index) => {
              if (index === selectedWeekIndex) {
                return [...(week || []), ...newRoutines];
              }
              return week;
            });
      return {
        ...updatedProgram,
        programItems: updatedProgramItems,
      };
    });
  };

  const renderAddWorkoutDialog = () => {
    if (!isAddWorkoutDialogOpen) return null;
    return (
      <AddWorkoutDialog
        onCreate={() => {
          // Create a new workout flow
          setIsAddWorkoutDialogOpen(false);
          const dayIndex =
            programState.programItems?.[selectedWeekIndex]?.length || 0;
          setEditedWorkoutWeekIndex(selectedWeekIndex);
          setEditedWorkoutDayIndex(dayIndex);
          setEditedRoutine({
            name: '',
            description: '',
            exerciseGroups: [],
          } as TrainingRoutine);
          setEditedWorkoutDay(selectedDay);
          setIsAddNewWorkoutFlow(true);
        }}
        onAdd={() => {
          setIsAddWorkoutDialogOpen(false);
          setIsAddWorkoutsDialogOpen(true);
        }}
        onClose={() => {
          setSelectedDay(null);
          setIsAddWorkoutDialogOpen(false);
        }}
      />
    );
  };

  const renderAddWorkoutsDialog = () => {
    if (!isAddWorkoutsDialogOpen) return null;
    return (
      <AddWorkoutsDialog
        onSubmit={(workouts: TrainingRoutineTemplate[]) => {
          handleAddWorkoutsFromTemplates(workouts);
          setIsAddWorkoutsDialogOpen(false);
        }}
        onClose={() => setIsAddWorkoutsDialogOpen(false)}
        singleSelect={true}
      />
    );
  };

  const onRemoveProgramItem = (weekIndex: number, programItemIndex: number) => {
    updateProgramState((prevProgram) => {
      const updatedProgramItems = prevProgram.programItems.map(
        (week, index) => {
          if (index === weekIndex) {
            return week.filter(
              (_, itemIndex) => itemIndex !== programItemIndex,
            );
          }
          return week;
        },
      );

      return {
        ...prevProgram,
        programItems: updatedProgramItems,
      };
    });
  };

  const onAddWorkoutFromCalendar = (weekIndex: number, day: Day | null) => {
    if (day === null) return;
    setSelectedWeekIndex(weekIndex);
    setSelectedDay(day);
    setIsAddWorkoutDialogOpen(true);
  };

  const openEditWorkout = (
    routine: TrainingRoutine,
    weekIndex: number,
    dayIndex: number,
    day: Day | null,
  ) => {
    setEditedWorkoutWeekIndex(weekIndex);
    setEditedWorkoutDayIndex(dayIndex);
    setEditedRoutine(routine);
    setEditedWorkoutDay(day || null);
    setIsAddNewWorkoutFlow(false);
  };

  const handleSubmitEditedRoutine = useCallback(
    async (routine: TrainingRoutine) => {
      const currentState = programStateRef.current;
      const shouldAddNewWorkout =
        isAddNewWorkoutFlow &&
        editedWorkoutWeekIndex !== null &&
        editedWorkoutDayIndex !== null &&
        editedWorkoutDayIndex ===
        (currentState?.programItems?.[editedWorkoutWeekIndex]?.length || 0);

      const existingProgramItems = currentState?.programItems || [];
      const updatedProgramItems = existingProgramItems.map(
        (week, weekIndex) =>
          (week || []).map((programItem, dayIndex) =>
            weekIndex === editedWorkoutWeekIndex &&
            dayIndex === editedWorkoutDayIndex
              ? {
                  ...programItem,
                  routine: routine,
                  day: editedWorkoutDay,
                }
              : programItem,
          ),
      );

      if (shouldAddNewWorkout && editedWorkoutWeekIndex !== null) {
        while (updatedProgramItems.length <= editedWorkoutWeekIndex) {
          updatedProgramItems.push([]);
        }
        updatedProgramItems[editedWorkoutWeekIndex].push({
          routine: routine,
          day: editedWorkoutDay,
        });
      }

      const newProgramData = {
        ...currentState,
        programItems: updatedProgramItems,
      } as WithOptionalId<AssignedProgram>;

      updateProgramState(newProgramData);
      setEditedWorkoutWeekIndex(null);
      setEditedWorkoutDayIndex(null);
      setEditedRoutine(null);
      setEditedWorkoutDay(null);
      setIsAddNewWorkoutFlow(false);
      return Promise.resolve(true);
    },
    [
      isAddNewWorkoutFlow,
      editedWorkoutWeekIndex,
      editedWorkoutDayIndex,
      editedWorkoutDay,
    ],
  );

  const renderHeader = () => {
    return (
      <div className={styles.header}>
        <div className={styles.nameEdit}>
          <EditName
            value={name}
            placeholder={texts.titlePlaceholder}
            onChange={setName}
            onCommit={(next) => {
              updateProgramState((prevProgram) => ({
                ...prevProgram,
                name: next,
              }));
            }}
          />
        </div>
        <div className={styles.toggleViewButtons}>
          <Button
            className={classNames(styles.toggleViewButton, {
              [styles.selectedView]: viewMode === ViewModes.CALENDAR,
            })}
            onClick={() => setViewMode(ViewModes.CALENDAR)}
          >
            <CalendarMonthIcon color="primary" />
          </Button>
          <Button
            className={classNames(styles.toggleViewButton, {
              [styles.selectedView]: viewMode === ViewModes.WEEK,
            })}
            onClick={() => {
              if (weeksDates) setSelectedWeekIndex(currentWeekIndex);
              setViewMode(ViewModes.WEEK);
            }}
          >
            <FormatListBulletedIcon color="primary" />
          </Button>
        </div>
      </div>
    );
  };

  const duplicateWeek = (weekIndex: number) => {
    if (weekIndex >= programState.programItems.length) {
      return;
    }

    updateProgramState((prevProgram) => ({
      ...prevProgram,
      programItems: [
        ...prevProgram.programItems.slice(0, weekIndex + 1),
        prevProgram.programItems[weekIndex].map((programItem) => ({
          routine: _.cloneDeep(programItem.routine),
          day: programItem.day,
        })),
        ...prevProgram.programItems.slice(weekIndex + 1),
      ],
    }));
    setSelectedWeekIndex(weekIndex + 1);
  };

  const moveWorkoutToDay = (
    fromWeekIndex: number,
    fromDay: Day,
    toWeekIndex: number,
    toDay: Day,
  ) => {
    updateProgramState((prevProgram) => {
      if (fromWeekIndex === toWeekIndex && fromDay === toDay) {
        return prevProgram;
      }
      const sourceWeek = prevProgram.programItems[fromWeekIndex];
      if (!sourceWeek) return prevProgram;
      const sourceIndex = sourceWeek.findIndex((it) => it.day === fromDay);
      if (sourceIndex === -1) return prevProgram;

      const targetWeek = prevProgram.programItems[toWeekIndex] || [];
      if (targetWeek.some((it) => it.day === toDay)) return prevProgram;

      const updatedProgramItems = prevProgram.programItems.map((week) => [
        ...(week || []),
      ]);
      const [itemToMove] = updatedProgramItems[fromWeekIndex].splice(
        sourceIndex,
        1,
      );

      while (updatedProgramItems.length <= toWeekIndex) {
        updatedProgramItems.push([]);
      }
      updatedProgramItems[toWeekIndex].push({
        ...itemToMove,
        day: toDay,
      });

      return {
        ...prevProgram,
        programItems: updatedProgramItems,
      };
    });
  };

  const deleteWeek = (weekIndex: number) => {
    if (weekIndex >= programState.programItems.length) {
      return;
    }

    const isFirstWeek = weekIndex === 0;
    const isSingleWeek = isFirstWeek && programState.programItems.length === 1;
    if (isSingleWeek) return;
    updateProgramState((prevProgram) => ({
      ...prevProgram,
      programItems: prevProgram.programItems.filter(
        (_, index) => index !== weekIndex,
      ),
    }));
    if (isFirstWeek) return;
    setSelectedWeekIndex(weekIndex - 1);
  };

  const renderCalendarView = () => {
    if (!weeksDates) return null;
    return (
      <ProgramCalendar
        program={programState}
        weeksDates={weeksDates}
        setSelectedWeekIndex={setSelectedWeekIndex}
        onAddWorkout={(w, d) => onAddWorkoutFromCalendar(w, d)}
        onEditWorkout={(w, d) => {
          const itemIndex = (programState.programItems[w] || []).findIndex(
            (it) => it.day === d,
          );
          if (itemIndex === -1) return;
          openEditWorkout(
            programState.programItems[w][itemIndex].routine,
            w,
            itemIndex,
            d,
          );
        }}
        onRemoveWorkout={(w, d) => {
          const itemIndex = (programState.programItems[w] || []).findIndex(
            (it) => it.day === d,
          );
          if (itemIndex === -1) return;
          onRemoveProgramItem(w, itemIndex);
        }}
        onDuplicateWeek={(weekIndex) => duplicateWeek(weekIndex)}
        onDeleteWeek={(weekIndex) => deleteWeek(weekIndex)}
        onMoveWorkout={moveWorkoutToDay}
        draggingWorkout={draggingWorkout}
        setDraggingWorkout={setDraggingWorkout}
      />
    );
  };

  const renderWeekView = () => {
    return (
      <ProgramWeek
        weekIndex={selectedWeekIndex}
        weeksDates={weeksDates}
        setWeekIndex={setSelectedWeekIndex}
        program={programState as AssignedProgram}
        onAddWorkout={(w, d) => onAddWorkoutFromCalendar(w, d)}
        onEditWorkout={(w, d) => {
          const itemIndex = (programState.programItems[w] || []).findIndex(
            (it) => it.day === d,
          );
          if (itemIndex === -1) return;
          openEditWorkout(
            programState.programItems[w][itemIndex].routine,
            w,
            itemIndex,
            d,
          );
        }}
        onRemoveProgramItem={(w, d) => {
          const itemIndex = (programState.programItems[w] || []).findIndex(
            (it) => it.day === d,
          );
          if (itemIndex === -1) return;
          onRemoveProgramItem(w, itemIndex);
        }}
        onDuplicateWeek={(weekIndex) => duplicateWeek(weekIndex)}
        onDeleteWeek={(weekIndex) => deleteWeek(weekIndex)}
        onMoveWorkout={moveWorkoutToDay}
        draggingWorkout={draggingWorkout}
        setDraggingWorkout={setDraggingWorkout}
      />
    );
  };

  const renderView = () => {
    switch (viewMode) {
      case ViewModes.WEEK:
        return renderWeekView();
      case ViewModes.CALENDAR:
        return renderCalendarView();
      default:
        return renderCalendarView();
    }
  };

  const renderFooter = () => {
    return (
      <div className={styles.footer}>
        <Button
          className={classNames(styles.submit, styles.action)}
          onClick={() => handleSubmit()}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="info" />
          ) : (
            <span className={styles.submitText}>Save Program</span>
          )}
        </Button>
        <Button
          className={classNames(styles.cancel, styles.action)}
          onClick={onClose}
          disabled={loading}
        >
          <span className={styles.cancelText}>Cancel</span>
        </Button>
      </div>
    );
  };

  const renderErrorMessage = () => {
    if (!errorMessage) return null;
    return (
      <Snackbar
        open={true}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    );
  };

  const mainContent = (
    <div className={styles.container}>
      {renderHeader()}
      {renderView()}
      {renderFooter()}
      {renderErrorMessage()}
      {renderAddWorkoutDialog()}
      {renderAddWorkoutsDialog()}
    </div>
  );

  const detailContent = editedRoutine ? (
    <EditRoutine
      onSubmit={handleSubmitEditedRoutine}
      routine={editedRoutine}
      setRoutine={(updater) => {
        setEditedRoutine((prev) => {
          if (typeof updater === 'function') {
            if (!prev) return prev as any;
            return (updater as (r: TrainingRoutine) => TrainingRoutine)(prev);
          }
          return updater as TrainingRoutine;
        });
      }}
      onClose={() => {
        setEditedRoutine(null);
      }}
    />
  ) : null;

  return (
    <ContentDrill
      showHeader={false}
      isDrilledIn={Boolean(editedRoutine)}
      drillInContent={detailContent}
      disableSearch
    >
      {mainContent}
    </ContentDrill>
  );
};

export default EditProgram;
